import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRentangTanggal } from "@/lib/datetime";
import { MAKS_PERIODE_CUTI, periksaPeriodeCuti, samaPeriode, tahunBerjalanWIB, urutkanPeriode } from "@/lib/periodeCuti";

// Bagian "Periode Cuti" di dialog Edit Peserta (Okt 2026). Disimpan TERPISAH dari "Simpan Perubahan" lewat
// PUT /peserta/{id}/periode-cuti (seperti koreksi hari Stase RSCM), jadi tidak terhalang aturan biodata wajib.
// Hanya tanggal (tanpa alasan). Bulan yang SELURUH harinya tercakup periode cuti tidak wajib diisi Rotasi Klinik.
let nomorBaris = 0;
const barisBaru = (p = { mulai: "", akhir: "" }) => ({ kunci: `cuti-${(nomorBaris += 1)}`, mulai: p.mulai, akhir: p.akhir });
const tanpaKunci = (baris) => baris.map(({ mulai, akhir }) => ({ mulai, akhir }));

export default function PeriodeCutiSection({ pesertaId, periodeAwal, locked = false, onTersimpan }) {
    const [tersimpan, setTersimpan] = useState(periodeAwal || []);
    const [baris, setBaris] = useState(() => (periodeAwal || []).map(barisBaru));
    const [galat, setGalat] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const sedangSimpan = useRef(false);

    useEffect(() => {
        setTersimpan(periodeAwal || []);
        setBaris((periodeAwal || []).map(barisBaru));
        setGalat(null);
    }, [pesertaId, periodeAwal]);

    const ubahBaris = (kunci, field, nilai) => {
        setBaris((lama) => lama.map((b) => (b.kunci === kunci ? { ...b, [field]: nilai } : b)));
        setGalat(null);
    };
    const tambah = () => { setBaris((lama) => [...lama, barisBaru()]); setGalat(null); };
    const hapus = (kunci) => { setBaris((lama) => lama.filter((b) => b.kunci !== kunci)); setGalat(null); };

    const isian = tanpaKunci(baris);
    const berubah = !samaPeriode(isian, tersimpan);

    const simpan = async () => {
        if (sedangSimpan.current) return;
        const pesan = periksaPeriodeCuti(isian, tahunBerjalanWIB());
        if (pesan) { setGalat(pesan); return; }
        sedangSimpan.current = true;
        setSibuk(true);
        try {
            const { data } = await api.put(`/peserta/${pesertaId}/periode-cuti`, { periode_cuti: urutkanPeriode(isian) });
            setTersimpan(data.periode_cuti);
            setBaris(data.periode_cuti.map(barisBaru));
            toast.success("Periode cuti disimpan");
            if (onTersimpan) onTersimpan(data.periode_cuti);
        } catch (e) {
            setGalat(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            sedangSimpan.current = false;
            setSibuk(false);
        }
    };

    return (
        <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="periode-cuti-section">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Periode Cuti</Label>
            <p className="text-[11px] text-slate-500 mt-1">
                Bulan yang seluruh harinya tercakup periode cuti tidak wajib diisi Rotasi Klinik. Cuti sebagian bulan tetap wajib.
            </p>
            <p className="text-xs text-slate-600 mt-2" data-testid="periode-cuti-ringkas">
                {tersimpan.length
                    ? `Tersimpan: ${tersimpan.map((p) => formatRentangTanggal(p.mulai, p.akhir)).join("; ")}`
                    : "Belum ada periode cuti."}
            </p>
            <div className="mt-2 space-y-2">
                {baris.map((b, i) => (
                    <div key={b.kunci} className="flex flex-wrap items-end gap-2" data-testid="periode-cuti-baris">
                        <div>
                            <Label className="text-[11px] text-slate-500" htmlFor={`${b.kunci}-mulai`}>Mulai</Label>
                            <Input id={`${b.kunci}-mulai`} type="date" className="w-40" value={b.mulai} disabled={locked || sibuk}
                                onChange={(e) => ubahBaris(b.kunci, "mulai", e.target.value)} data-testid={`periode-cuti-mulai-${i}`} />
                        </div>
                        <div>
                            <Label className="text-[11px] text-slate-500" htmlFor={`${b.kunci}-akhir`}>Akhir</Label>
                            <Input id={`${b.kunci}-akhir`} type="date" className="w-40" value={b.akhir} disabled={locked || sibuk}
                                onChange={(e) => ubahBaris(b.kunci, "akhir", e.target.value)} data-testid={`periode-cuti-akhir-${i}`} />
                        </div>
                        <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                            disabled={locked || sibuk} onClick={() => hapus(b.kunci)} aria-label={`Hapus periode cuti ke-${i + 1}`}
                            data-testid={`periode-cuti-hapus-${i}`}>
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                ))}
            </div>
            {galat && <p className="text-xs text-rose-600 mt-2" role="alert" data-testid="periode-cuti-galat">{galat}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
                <Button type="button" size="sm" variant="outline" onClick={tambah}
                    disabled={locked || sibuk || baris.length >= MAKS_PERIODE_CUTI} data-testid="periode-cuti-tambah">
                    <Plus className="w-4 h-4 mr-1" /> Tambah Periode
                </Button>
                <Button type="button" size="sm" className="bg-[#00B9AD] hover:bg-[#60C0D0]" onClick={simpan}
                    disabled={locked || sibuk || !berubah} data-testid="periode-cuti-simpan">
                    Simpan Periode Cuti
                </Button>
                {baris.length >= MAKS_PERIODE_CUTI && (
                    <span className="text-[11px] text-slate-500">Paling banyak {MAKS_PERIODE_CUTI} periode.</span>
                )}
            </div>
        </div>
    );
}
